import {inject, Injectable} from "@angular/core";
import {AuthService, collection, component, EntityData, field} from "@fedelta-pos/data-client";
import {DealData} from '../deal/deal-entity.model';
import {PaymentType} from '../payment/payment';
import {InitiatedOrderData, OrderInitiationData} from "./order-initiation.service";
import {OrderPlacementData, PlacedOrderData} from "./order-placement.service";
import {OrderingContextModel} from '../ordering-context/ordering-context.model';

export class RecentOrderData extends EntityData<RecentOrderData> {
    @field identityId: string;
    @field pending: boolean;
    @field convertedToSale: boolean;
    @field orderNotes: string;
    @component orderInitiation: OrderInitiationData;
    @component initiatedOrder: InitiatedOrderData;
    @component orderPlacement: OrderPlacementData;
    @component placedOrder: PlacedOrderData;
    @collection(DealData) deals: DealData[];

    // Backward compatibility for old orders

    // noinspection JSUnusedGlobalSymbols
    set paymentType(paymentType: PaymentType) {
        this.orderPlacement.paymentAuthorisation.paymentType = paymentType;
    }

    // noinspection JSUnusedGlobalSymbols
    set paymentCardBrand(paymentCardBrand: string) {
        this.orderPlacement.paymentAuthorisation.paymentCardBrand = paymentCardBrand;
    }

    // noinspection JSUnusedGlobalSymbols
    set paymentCardCountry(paymentCardCountry: string) {
        this.orderPlacement.paymentAuthorisation.paymentCardCountry = paymentCardCountry;
    }

    // noinspection JSUnusedGlobalSymbols
    set paymentCardLast4(paymentCardLast4: string) {
        this.orderPlacement.paymentAuthorisation.paymentCardLast4 = paymentCardLast4;
    }
}

export class RecentOrdersData extends EntityData<RecentOrdersData> {
    @collection(RecentOrderData) orders: RecentOrderData[];
}

/**
 * This service provides storage and retrieval of recent orders to and from local device storage.
 */
@Injectable({providedIn: "root"})
export class RecentOrderService {
    private authService: AuthService = inject(AuthService);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);

    public static readonly RECENT_ORDER_LOCAL_STORAGE_KEY = "recentOrders";
    public static readonly CURRENT_ORDER_LOCAL_STORAGE_KEY = "currentOrder";
    private static readonly CURRENT_ORDER_CUTOFF_MINUTES: number = 20;

    /**
     * Local storage keys which include a path prefix to prevent clashes with apps on the same domain but different path
     */
    private readonly recentOrderStorageKey: string;
    private readonly currentOrderStorageKey: string;

    constructor() {
        console.log("--- RecentOrderService");

        this.recentOrderStorageKey = window.location.pathname + "/" + RecentOrderService.RECENT_ORDER_LOCAL_STORAGE_KEY;
        this.currentOrderStorageKey = window.location.pathname + "/" + RecentOrderService.CURRENT_ORDER_LOCAL_STORAGE_KEY;
    }

    public addRecentOrder(recentOrder: RecentOrderData): void {
        recentOrder.identityId = this.authService.getLoggedInIdentityId();
        let recentOrders: RecentOrdersData = <RecentOrdersData>EntityData.create(RecentOrdersData);
        recentOrders.assign(JSON.parse(localStorage.getItem(this.recentOrderStorageKey)) || {});
        let index: number = recentOrders.orders.findIndex((recentOrder: RecentOrderData) => recentOrder.initiatedOrder.requestId === recentOrder.initiatedOrder.requestId);
        if (index == -1) {
            recentOrders.orders.unshift(recentOrder);
        } else {
            recentOrders.orders[index] = recentOrder;
        }
        try {
            localStorage.setItem(this.recentOrderStorageKey, recentOrders.toJSONString());
        } catch (e) {
            console.log("error saving recent orders", e);
        }
    }

    public get recentOrders(): RecentOrderData[] {
        let recentOrders: RecentOrdersData = <RecentOrdersData>EntityData.create(RecentOrdersData);
        recentOrders.assign(JSON.parse(localStorage.getItem(this.recentOrderStorageKey)) || {});
        return recentOrders.orders.filter((recentOrder: RecentOrderData) => (recentOrder.identityId === this.authService.getLoggedInIdentityId() && !recentOrder.pending));
    }

    public get recentPendingOrders(): RecentOrderData[] {
        let recentOrders: RecentOrdersData = <RecentOrdersData>EntityData.create(RecentOrdersData);
        recentOrders.assign(JSON.parse(localStorage.getItem(this.recentOrderStorageKey)) || {});
        return recentOrders.orders.filter((recentOrder: RecentOrderData) => (recentOrder.identityId === this.authService.getLoggedInIdentityId() && recentOrder.pending));
    }

    /**
     * Remove from local storage any order whose estimated pickup time is more than one day ago, or whose lookup code is on a recent sale (except for the most recent order for reordering).
     * @param lookupCodes
     */
    public purgeOldOrders(lookupCodes: string[]) {
        let recentOrders: RecentOrdersData = <RecentOrdersData>EntityData.create(RecentOrdersData);
        recentOrders.assign(JSON.parse(localStorage.getItem(this.recentOrderStorageKey)) || {});
        if (recentOrders.orders && recentOrders.orders.length) {
            let expiry: Date = new Date();
            expiry.setDate(expiry.getDate() - 1);
            let expiryTime: number = expiry.getTime();
            recentOrders.orders = recentOrders.orders.filter((recentOrder: RecentOrderData, i: number) => {
                const orderExpired: boolean = (recentOrder.placedOrder.estimatedCompletionDateTime || recentOrder.placedOrder.placedDateTime || recentOrder.initiatedOrder.initiatedDateTime).getTime() <= expiryTime;
                if (lookupCodes.some((lookupCode: string) => lookupCode === recentOrder.placedOrder.lookupCode)) {
                    recentOrder.convertedToSale = true;
                }
                return i == 0 || recentOrder.pending || (!orderExpired && !recentOrder.convertedToSale);
            });
            localStorage.setItem(this.recentOrderStorageKey, recentOrders.toJSONString());
        }
    }

    public deletePendingOrder(requestId: string): void {
        let recentOrders: RecentOrdersData = <RecentOrdersData>EntityData.create(RecentOrdersData);
        recentOrders.assign(JSON.parse(localStorage.getItem(this.recentOrderStorageKey)) || {});
        let index: number = recentOrders.orders.findIndex((recentOrder: RecentOrderData) => recentOrder.initiatedOrder.requestId === requestId);
        if (index != -1) {
            recentOrders.orders.splice(index, 1);
            try {
                localStorage.setItem(this.recentOrderStorageKey, recentOrders.toJSONString());
            } catch (e) {
                console.log("error saving recent orders", e);
            }
        }
    }

    public saveCurrentOrder(orderInitiation: OrderInitiationData, orderNotes: string, deals: DealData[]) {
        const recentOrder: RecentOrderData = <RecentOrderData>EntityData.create(RecentOrderData);
        recentOrder.orderInitiation = orderInitiation;
        recentOrder.orderNotes = orderNotes;
        recentOrder.deals = deals;
        recentOrder.initiatedOrder = <InitiatedOrderData>EntityData.create(InitiatedOrderData);
        recentOrder.initiatedOrder.initiatedDateTime = new Date();
        recentOrder.identityId = this.authService.getLoggedInIdentityId();
        try {
            localStorage.setItem(this.currentOrderStorageKey, recentOrder.toJSONString());
        } catch (e) {
            console.log("error saving current order", e);
        }
    }

    public keepCurrentOrderAlive(): void {
        const persisted: string = localStorage.getItem(this.currentOrderStorageKey);
        if (!persisted) {
            return;
        }
        const recentOrder: RecentOrderData = <RecentOrderData>EntityData.create(RecentOrderData).assign(JSON.parse(persisted) || {});
        recentOrder.initiatedOrder.initiatedDateTime = new Date();
        localStorage.setItem(this.currentOrderStorageKey, recentOrder.toJSONString());
    }

    public getCurrentOrder(): RecentOrderData {
        const persisted: string = localStorage.getItem(this.currentOrderStorageKey);
        if (!persisted) {
            return undefined;
        }
        const recentOrder: RecentOrderData = <RecentOrderData>EntityData.create(RecentOrderData).assign(JSON.parse(persisted) || {});
        if (!recentOrder.initiatedOrder || !recentOrder.orderInitiation) {
            return undefined;
        }
        let expiry: Date = new Date();
        expiry.setMinutes(expiry.getMinutes() - RecentOrderService.CURRENT_ORDER_CUTOFF_MINUTES);
        let expiryTime: number = expiry.getTime();
        if (recentOrder.identityId === this.authService.getLoggedInIdentityId() &&
            (!this.orderingContextModel.orderType() || recentOrder.orderInitiation.orderTypeEnum === this.orderingContextModel.orderType()) &&
            recentOrder.initiatedOrder.initiatedDateTime.getTime() > expiryTime) {
            return recentOrder;
        } else {
            this.deleteCurrentOrder();
            return undefined;
        }
    }

    public deleteCurrentOrder(): void {
        localStorage.removeItem(this.currentOrderStorageKey);
    }
}
